import React, { useEffect, useMemo, useRef, useState } from "react"

import defaultAvatar from "@/assest/default_avatar.png"
import icEnter from "@/assest/ic_enter.png"
import icError from "@/assest/ic_error.png"
import icUser from "@/assest/ic_user.png"
import icSettingTwo from "@/assest/ic_setting_two.png"
import icResume from "@/assest/ic_resume.png"
import icStop from "@/assest/ic_stop.png"

import { BizError, userDisplayName } from "@/models/biz_model"
import { store, useAppDispatch, useAppSelector } from "@/store"
import {
  LoginApiVersion,
  LoginStatus,
  StopResumeType,
  ViewType,
  changeViewType,
  selectCanRestart,
  selectChangePasswordPrompt,
  selectImgCodeObj,
  selectLoginState,
  setLoginApiVersion,
  setStopResumeType,
  setUsernameInput,
  updateLoginStatus,
  userAutoLogin,
} from "@/stores/login_slice"
import { ConnectionUseCase, ExitReason } from "@/usecases/connection"
import { UserUsecase } from "@/usecases/user"
import { AuthCache, AutoLoginCache, UserCache } from "@/utils/storage"
import { AppIconButton } from "@/widgets/Common"
import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  ToastId,
  Tooltip,
  UseToastOptions,
  useToast,
} from "@chakra-ui/react"
import { useLoginNew } from "./login_hook"
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons"
import "./passwordInputStyles.css"
import { AboutContent } from "./about_content"
import { isProdEnv } from "@/config/env"
import { goToResetPassword } from "@/utils/platform"
import { selectFpHash, setLoginPwd } from "@/stores/auth_slice"
import { useNavigate } from "react-router-dom"

const LoginCom: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const setErrMsg = (msg: string) => {
    dispatch(updateLoginStatus({ msg, status: LoginStatus.error }))
  }
  const { currentUser, msg, status } = useAppSelector(selectLoginState)
  const fpHash = useAppSelector(selectFpHash)
  // const [passVerifyMsg, setPassVerifyMsg] = useState<string>("")

  const imgCodeObj = useAppSelector(selectImgCodeObj)
  const canRestart = useAppSelector(selectCanRestart)

  const toast = useToast()
  const toastId = useRef<ToastId | null>(null)

  useEffect(() => {
    if (status !== LoginStatus.error || !msg) {
      return
    }

    const toastOption: UseToastOptions = {
      title: msg,
      duration: 3000,
      containerStyle: {
        marginBottom: "210px",
      },
      render: () => (
        <Flex
          background="rgba(0, 0, 0, 0.64)"
          paddingX="10px"
          paddingY="8px"
          borderRadius="8px"
          alignItems="center"
        >
          <Image src={icError} width="20px" height="20px" marginRight="10px" />
          <Text color="white">{msg}</Text>
        </Flex>
      ),
    }

    if (toastId.current && toast.isActive(toastId.current)) {
      toast.close(toastId.current)
    }
    toastId.current = toast(toastOption)
    // 清除已展示的错误提示
    dispatch(updateLoginStatus({ msg: "" }))
  }, [msg, status])

  useEffect(() => {
    const dispose = () => {
      // 离开页面时清除 toast 消息
      toast.closeAll()
    }
    return dispose
  }, [])

  const [account, setAccount] = useState<null | string>(
    currentUser?.account ?? null
  )

  const [password, setPassWord] = useState<null | string>(null)
  const stopResumeHandle = async (type: StopResumeType) => {
    if (account === null && currentUser?.account === null) {
      setErrMsg(`请选择要${type === "stop" ? "关机" : "重启"}的云电脑账号`)
      return
    }

    dispatch(setStopResumeType(type))
    dispatch(changeViewType(ViewType.stopResumeVerify))
    dispatch(setLoginApiVersion(LoginApiVersion.oldApi))
  }

  const [showViewIcon, setShowViewIcon] = useState(false)

  const showMsg = useMemo(() => {
    return msg.length > 0
  }, [msg])
  // 是否获取到已登录过的用户信息
  const hasUser = useMemo(() => currentUser !== null, [currentUser])
  // 是否展示企业登录入口
  const showKSLogin = useMemo(
    () => (!showMsg || msg === "正在努力连接中，请稍后...") && !hasUser,
    [showMsg, msg, hasUser]
  )

  // 是否能够切换用户
  const canChangeUser = UserCache.getUsers().length >= 1
  // 头像
  const avatarSrc = useMemo(
    () => currentUser?.avatar || defaultAvatar,
    [currentUser]
  )

  const loginNew = useLoginNew()
  const autoLoginAvailable = Boolean(
    currentUser &&
      currentUser.loginType !== "PC" &&
      fpHash &&
      AuthCache.getAuth(currentUser.account) &&
      AutoLoginCache.getConfig(currentUser.account).enabled &&
      AutoLoginCache.getConfig(currentUser.account).deviceSecret &&
      !AutoLoginCache.getConfig(currentUser.account).disabledByAdmin
  )

  const handleAutoLogin = async () => {
    if (ConnectionUseCase.isPeerConnecting()) return
    try {
      await dispatch(userAutoLogin()).unwrap()
      // 若已弹出修改初始密码提示，停留在登录页等用户决策，暂不跳转
      if (selectChangePasswordPrompt(store.getState())) {
        return
      }
      navigate("/remoteDesk")
    } catch (error) {
      const code =
        typeof error === "object" && error !== null && "code" in error
          ? (error as { code?: number }).code
          : undefined
      if ([-10026, -10027, -10028].includes(code ?? 0) && currentUser) {
        AutoLoginCache.updateConfig(currentUser.account, {
          enabled: false,
          ...(code === -10027 ? { deviceSecret: undefined } : {}),
          ...(code === -10028 ? { disabledByAdmin: true } : {}),
        })
      }
    }
  }

  const triggerAutoLogin = () => {
    handleAutoLogin().catch(console.error)
  }
  // 账号密码登录
  const handleSubmit = async () => {
    if (!hasUser && !account) {
      setErrMsg("请输入账号！")
      return
    }
    if (!password) {
      setErrMsg("请输入密码！")
      return
    }

    if (account === password) {
      setErrMsg("密码不能和账号相同")
      return
    }

    if (ConnectionUseCase.isPeerConnecting()) {
      // 正在连接中，避免重复连接
      return
    }

    // 关闭正在展示的错误提示语
    if (toastId.current && toast.isActive(toastId.current)) {
      toast.close(toastId.current)
    }

    dispatch(setLoginPwd(password))
    // 账号密码登录
    loginNew({ userName: account!, pwd: password, ...imgCodeObj })
    dispatch(setUsernameInput(account!))
  }

  // 跳转到企业登录页面
  const gotoKSLogin = async () => {
    const res = await UserUsecase.startKSLogin()
    if (res instanceof BizError) {
      setErrMsg(res.displayErrorMsg)
    }
  }
  const handleGotoKSLogin = () => {
    void gotoKSLogin()
  }

  // const copyUserAgent = async () => {
  //   try {
  //     await navigator.clipboard.writeText(navigator.userAgent)
  //     toast({
  //       title: "已复制",
  //       status: "success",
  //       duration: 2000,
  //       isClosable: true,
  //     })
  //   } catch {
  //     toast({
  //       title: "复制失败，请检查剪贴板权限",
  //       status: "error",
  //       duration: 3000,
  //       isClosable: true,
  //     })
  //   }
  // }

  const forgetPass = () => {
    goToResetPassword()
  }

  // 跳转到选择登录账号页面
  const gotoChangeUser = () => {
    if (ConnectionUseCase.isPeerConnecting()) {
      // 断开正在连接的设备（取消正在进行的连接）
      ConnectionUseCase.exitRemoteDesk(ExitReason.abortConnecting)
    }
    dispatch(changeViewType(ViewType.selectUser))
  }

  const loginMsg = status === LoginStatus.logging ? msg : ""

  return (
    <>
      <Flex direction="column" width="100vw" height="100vh" alignItems="center">
        {/* <Flex alignItems="center" gap="8px" maxWidth="90%">
          <Text wordBreak="break-all">{navigator.userAgent}</Text>
          <Button
            flexShrink={0}
            size="sm"
            color="white"
            variant="outline"
            onClick={() => {
              void copyUserAgent()
            }}
          >
            复制
          </Button>
        </Flex> */}

        {/* 用户登录 */}
        <Image
          src={avatarSrc}
          alt="avatar"
          width="88px"
          height="88px"
          marginTop="26vh"
          marginBottom="16px"
        />
        {!hasUser ? (
          // 账号输入框(未登录过的新用户)
          <LoginInput
            onChange={(e) => {
              setAccount(e.target.value.trim() || null)
            }}
          />
        ) : (
          // 用户头像和用户名(已登录过)
          <Text fontSize="15px" color="white">
            {userDisplayName(currentUser!)}
          </Text>
        )}
        <Box height="16px" />

        {autoLoginAvailable ? (
          <Button
            width="145px"
            height="34px"
            color="white"
            border="1px solid white"
            background="rgba(0, 0, 0, 0.75)"
            _hover={{ background: "rgba(0, 0, 0, 0.9)" }}
            onClick={triggerAutoLogin}
            isLoading={status === LoginStatus.logging}
          >
            登录
          </Button>
        ) : (
          <LoginInput
            type={showViewIcon ? "text" : "password"}
            value={password || ""}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleSubmit()
            }}
            onChange={(e) => {
              const pwdStr = e.target.value.trim()

              // if (pwdStr) {
              //   // 弱密码校验
              //   const validationResult: PasswordValidationResult =
              //     validatePassword(pwdStr, account! as string)
              //   if (!validationResult.isValid) {
              //     setPassVerifyMsg(`${validationResult.errors.join(", ")}`)
              //   } else {
              //     setPassVerifyMsg("")
              //   }
              // } else {
              //   setPassVerifyMsg("")
              // }

              setPassWord(pwdStr || null)
            }}
            showViewIcon={showViewIcon}
            handleShowViewIcon={() => setShowViewIcon(!showViewIcon)}
            suffix={
              <AppIconButton
                width="40px"
                height="40px"
                padding="0px"
                src={icEnter}
                onClick={() => {
                  handleSubmit()
                }}
              />
            }
          />
        )}

        {/* {passVerifyMsg && (
          <Text
            color="red"
            fontSize="13px"
            height={"22px"}
            backgroundColor="#ebe6e6e6"
            borderRadius="9px"
            padding="2px 15px 2px 15px"
            marginTop="7px"
          >
            {passVerifyMsg}
          </Text>
        )} */}

        <Box height="8px" />

        {hasUser ? (
          // 已登录过,并且是账号密码登录
          <Button
            color="white"
            variant={"link"}
            onClick={forgetPass}
            marginTop={"4px"}
            fontWeight={"normal"}
            fontSize="14px"
          >
            重置密码
          </Button>
        ) : (
          // 未登录过的新用户
          <></>
        )}

        {showMsg && (
          // 登录状态消息提示
          <Text margin="0px auto" fontSize="13px" textColor="white">
            {loginMsg}
          </Text>
        )}

        {showKSLogin && (
          <Flex justifyContent="space-between" width="230px">
            <Button
              color="white"
              variant={"link"}
              onClick={forgetPass}
              marginTop={"4px"}
              fontWeight={"normal"}
              fontSize="14px"
            >
              重置密码
            </Button>
            <Button
              color="white"
              variant={"link"}
              onClick={handleGotoKSLogin}
              marginTop={"4px"}
              fontWeight={"normal"}
              fontSize="14px"
            >
              企业登录
            </Button>
          </Flex>
        )}
        <div style={{ flex: 1 }} />
        <Flex
          width={canChangeUser && hasUser ? "120px" : "50px"}
          justifyContent="space-between"
        >
          {canChangeUser && (
            <Flex
              alignItems={"center"}
              justifyContent={"center"}
              direction="column"
              onClick={gotoChangeUser}
              cursor="pointer"
            >
              <Image src={icUser} alt="" width="32px" marginBottom="4px" />
              <Text color="#e8e8e8" fontSize="12px" marginBottom="16px">
                切换用户
              </Text>
            </Flex>
          )}
          {hasUser && (
            <Popover placement="top">
              <PopoverTrigger>
                <Flex
                  tabIndex={1}
                  alignItems={"center"}
                  justifyContent={"center"}
                  direction="column"
                  cursor="pointer"
                >
                  <Image
                    src={icSettingTwo}
                    alt=""
                    width="32px"
                    marginBottom="4px"
                  />
                  <Text color="#e8e8e8" fontSize="12px" marginBottom="16px">
                    设置
                  </Text>
                </Flex>
              </PopoverTrigger>
              <PopoverContent
                color="white"
                bg="gray.600"
                borderColor="gray.600"
                width={"120px"}
              >
                <PopoverArrow bg="gray.600" borderColor="gray.600" />
                <PopoverBody>
                  {canRestart ? (
                    <ImageText
                      src={icResume}
                      text="云电脑重启"
                      onClick={() => {
                        stopResumeHandle("resume")
                      }}
                      marginBottom="8px"
                    />
                  ) : (
                    <Tooltip
                      label={
                        <Flex direction="column" align="center">
                          <Text>当前云电脑不支持重启，</Text>
                          <Text>请联系it管理员</Text>
                        </Flex>
                      }
                      placement="right"
                      hasArrow
                    >
                      <Flex
                        alignItems={"center"}
                        justifyContent={"center"}
                        direction="row"
                        marginBottom="8px"
                        cursor="not-allowed"
                        opacity={0.5}
                        pointerEvents="auto"
                      >
                        <Image src={icResume} alt="" width="23px" />
                        <Text
                          color="#e8e8e8"
                          fontSize="11px"
                          marginLeft="6px"
                        >
                          云电脑重启
                        </Text>
                      </Flex>
                    </Tooltip>
                  )}
                  <ImageText
                    src={icStop}
                    text="云电脑关机"
                    onClick={() => {
                      stopResumeHandle("stop")
                    }}
                  />
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </Flex>

        <Box marginLeft="auto">
          {!isProdEnv() ? <AboutContent /> : <Box></Box>}
        </Box>
      </Flex>
    </>
  )
}

export default LoginCom

interface LoginInputProps extends InputProps {
  suffix?: React.ReactNode
  showViewIcon?: boolean
  handleShowViewIcon?: () => void
}

const LoginInput: React.FC<LoginInputProps> = ({
  placeholder,
  onChange,
  suffix,
  type,
  style: extraStyle,
  showViewIcon,
  handleShowViewIcon,
  ...extraProps
}) => {
  return (
    <Flex
      width="280px"
      height="40px"
      overflow="hidden"
      borderRadius="20px"
      alignItems="center"
      backgroundColor="rgba(255, 255, 255, 0.36)"
    >
      <InputGroup size="md" flex={1} height="100%">
        <Input
          variant="unstyled"
          style={extraStyle}
          fontSize="13px"
          color="black.400"
          padding="0px 0px 0px 16px"
          boxSizing="border-box"
          outline="none"
          backgroundColor="rgba(255, 255, 255, 0.7)"
          placeholder={suffix ? "请输入密码" : "请输入账号"}
          _placeholder={{ color: "black.400" }}
          type={type}
          onChange={onChange}
          {...extraProps}
        />

        <InputRightElement>
          {suffix ? (
            <IconButton
              variant="text"
              aria-label={showViewIcon ? "Mask password" : "Reveal password"}
              icon={
                showViewIcon ? (
                  <ViewOffIcon color="blue.500" />
                ) : (
                  <ViewIcon color="blue.500" />
                )
              }
              onClick={handleShowViewIcon}
            />
          ) : (
            <></>
          )}
        </InputRightElement>
      </InputGroup>
      {suffix ? <Box>{suffix}</Box> : <></>}
    </Flex>
  )
}

interface ImageTextProps {
  src: string
  onClick: () => void
  text: string
  marginBottom?: string
}
const ImageText: React.FC<ImageTextProps> = ({
  onClick,
  src,
  text,
  marginBottom,
}) => {
  return (
    <Flex
      alignItems={"center"}
      justifyContent={"center"}
      direction="row"
      marginBottom={marginBottom}
      onClick={onClick}
      cursor="pointer"
    >
      <Image src={src} alt="" width="23px" />
      <Text color="#e8e8e8" fontSize="11px" marginLeft="6px">
        {text}
      </Text>
    </Flex>
  )
}
